import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Tooltip,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  Avatar,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Chat as ChatIcon,
  WhatsApp as WhatsAppIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

interface Atendimento {
  id: number;
  lead_id: number;
  consultor_id: number | null;
  status: string;
  chat_session_id: number | null;
  conversation_id: number | null;
  channel: string | null;
  observacoes: string | null;
  created_at: string;
  started_at: string | null;
  ended_at: string | null;
}

interface Lead {
  id: number;
  nome: string;
  email: string;
  telefone: string;
  origem: string;
  status: string;
  score: number;
  created_at: string;
}

interface ChatMessage {
  id: number;
  session_id: number;
  content: string;
  is_from_user: boolean;
  created_at: string;
  message_type: string;
}

const FilaAtendimentos: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [aguardando, setAguardando] = useState<Atendimento[]>([]);
  const [emAtendimento, setEmAtendimento] = useState<Atendimento[]>([]);
  const [leads, setLeads] = useState<Record<number, Lead>>({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });
  const navigate = useNavigate();
  const [chatDialog, setChatDialog] = useState({
    open: false,
    messages: [] as ChatMessage[],
    title: '',
  });
  const leadsRef = useRef(leads);

  useEffect(() => {
    leadsRef.current = leads;
  }, [leads]);

  const fetchAtendimentos = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [aguardandoRes, emAtendimentoRes] = await Promise.all([
        axios.get(`${API_URL}/atendimentos/?status=aguardando`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/atendimentos/?status=em_atendimento`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setAguardando(aguardandoRes.data);
      // Para consultores, filtra só os que são deles. Para admins, mostra todos
      const filteredEmAtendimento = user?.role === 'admin'
        ? emAtendimentoRes.data
        : emAtendimentoRes.data.filter((a: Atendimento) => a.consultor_id === user?.id);
      setEmAtendimento(filteredEmAtendimento);

      // Buscar dados dos leads
      const allAtendimentos = [...aguardandoRes.data, ...emAtendimentoRes.data];
      const leadIds = allAtendimentos.map((a: Atendimento) => a.lead_id);
      const uniqueLeadIds = Array.from(new Set(leadIds));
      const leadsObj: Record<number, Lead> = { ...leadsRef.current };

      for (const id of uniqueLeadIds) {
        if (!leadsObj[id]) {
          const leadRes = await axios.get(`${API_URL}/leads/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          leadsObj[id] = leadRes.data;
        }
      }
      setLeads(leadsObj);
    } catch (err) {
      showSnackbar('Erro ao buscar atendimentos', 'error');
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated || (user?.role !== 'consultor' && user?.role !== 'admin')) {
      navigate('/login');
      return;
    }
    fetchAtendimentos();
  }, [isAuthenticated, user, navigate, fetchAtendimentos]);

  const assumirAtendimento = async (atendimentoId: number) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/atendimentos/${atendimentoId}/assumir?consultor_id=${user?.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSnackbar('Atendimento assumido com sucesso!', 'success');
      fetchAtendimentos();
    } catch (err) {
      showSnackbar('Erro ao assumir atendimento', 'error');
    }
    setLoading(false);
  };

  const finalizarAtendimento = async (atendimentoId: number) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/atendimentos/${atendimentoId}/finalizar`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSnackbar('Atendimento finalizado com sucesso!', 'success');
      fetchAtendimentos();
    } catch (err) {
      showSnackbar('Erro ao finalizar atendimento', 'error');
    }
    setLoading(false);
  };

  const openChatDialog = async (chatSessionId: number | null, leadName: string) => {
    if (!chatSessionId) return;
    
    setChatDialog({
      open: true,
      messages: [],
      title: `Histórico do Chat - ${leadName}`,
    });
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/chatbot/messages/${chatSessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatDialog(prev => ({ ...prev, messages: res.data }));
    } catch (err) {
      showSnackbar('Erro ao buscar histórico do chat', 'error');
    }
    setLoading(false);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const openWhatsAppInbox = (conversationId?: number | null) => {
    if (conversationId) {
      navigate(`/admin/whatsapp-inbox?conversationId=${conversationId}`);
      return;
    }
    navigate('/admin/whatsapp-inbox');
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'aguardando':
        return <Chip label="Aguardando" color="warning" icon={<PendingIcon />} size="small" />;
      case 'em_atendimento':
        return <Chip label="Em Atendimento" color="primary" icon={<ScheduleIcon />} size="small" />;
      case 'finalizado':
        return <Chip label="Finalizado" color="success" icon={<CheckCircleIcon />} size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  if (loading && aguardando.length === 0 && emAtendimento.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Fila de Atendimentos
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Gerencie os atendimentos em espera e em andamento
        </Typography>
      </Box>

      {/* Estatísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PendingIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{aguardando.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Aguardando
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ScheduleIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{emAtendimento.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Em Atendimento
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PersonIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {Object.keys(leads).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Leads Cadastrados
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CheckCircleIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {user?.full_name || 'Consultor'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Consultor Ativo
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Botão Atualizar */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAtendimentos}
            disabled={loading}
          >
            Atualizar Fila
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<WhatsAppIcon />}
            onClick={() => openWhatsAppInbox()}
          >
            Abrir Inbox WhatsApp
          </Button>
        </Box>
      </Box>

      {/* Aguardando Atendimento */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
        Aguardando Atendimento ({aguardando.length})
      </Typography>
      <Paper sx={{ width: '100%', overflow: 'hidden', mb: 4 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Lead</TableCell>
                <TableCell>Contato</TableCell>
                <TableCell>Origem</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Data de Criação</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {aguardando.map((atendimento) => {
                const lead = leads[atendimento.lead_id];
                return (
                  <TableRow key={atendimento.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                          <PersonIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {lead?.nome || 'Carregando...'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ID: {atendimento.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                          <EmailIcon fontSize="small" sx={{ mr: 1 }} />
                          <Typography variant="body2">
                            {lead?.email || '-'}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <PhoneIcon fontSize="small" sx={{ mr: 1 }} />
                          <Typography variant="body2">
                            {lead?.telefone || '-'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip label={lead?.origem || '-'} size="small" />
                        {atendimento.channel === 'whatsapp_web' && (
                          <Chip label="WhatsApp" size="small" color="success" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(atendimento.status)}
                    </TableCell>
                    <TableCell>
                      {formatDate(atendimento.created_at)}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Assumir atendimento">
                        <IconButton
                          color="success"
                          onClick={() => assumirAtendimento(atendimento.id)}
                          disabled={loading}
                          size="small"
                        >
                          <PlayIcon />
                        </IconButton>
                      </Tooltip>
                      {atendimento.chat_session_id && (
                        <Tooltip title="Ver histórico do chat">
                          <IconButton
                            color="info"
                            onClick={() => openChatDialog(atendimento.chat_session_id, lead?.nome || '')}
                            size="small"
                          >
                            <ChatIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {atendimento.channel === 'whatsapp_web' && atendimento.conversation_id && (
                        <Tooltip title="Abrir conversa na Inbox WhatsApp">
                          <IconButton
                            color="success"
                            onClick={() => openWhatsAppInbox(atendimento.conversation_id)}
                            size="small"
                          >
                            <WhatsAppIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {aguardando.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" color="text.secondary">
                      Nenhum atendimento aguardando
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Atendimentos em Andamento */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
        {user?.role === 'admin' ? 'Atendimentos em Andamento' : 'Meus Atendimentos em Andamento'} ({emAtendimento.length})
      </Typography>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Lead</TableCell>
                <TableCell>Contato</TableCell>
                <TableCell>Origem</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Iniciado em</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {emAtendimento.map((atendimento) => {
                const lead = leads[atendimento.lead_id];
                return (
                  <TableRow key={atendimento.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                          <PersonIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {lead?.nome || 'Carregando...'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ID: {atendimento.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                          <EmailIcon fontSize="small" sx={{ mr: 1 }} />
                          <Typography variant="body2">
                            {lead?.email || '-'}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <PhoneIcon fontSize="small" sx={{ mr: 1 }} />
                          <Typography variant="body2">
                            {lead?.telefone || '-'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip label={lead?.origem || '-'} size="small" />
                        {atendimento.channel === 'whatsapp_web' && (
                          <Chip label="WhatsApp" size="small" color="success" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(atendimento.status)}
                    </TableCell>
                    <TableCell>
                      {atendimento.started_at ? formatDate(atendimento.started_at) : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Finalizar atendimento">
                        <IconButton
                          color="error"
                          onClick={() => finalizarAtendimento(atendimento.id)}
                          disabled={loading}
                          size="small"
                        >
                          <StopIcon />
                        </IconButton>
                      </Tooltip>
                      {atendimento.chat_session_id && (
                        <Tooltip title="Ver histórico do chat">
                          <IconButton
                            color="info"
                            onClick={() => openChatDialog(atendimento.chat_session_id, lead?.nome || '')}
                            size="small"
                          >
                            <ChatIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {atendimento.channel === 'whatsapp_web' && atendimento.conversation_id && (
                        <Tooltip title="Abrir conversa na Inbox WhatsApp">
                          <IconButton
                            color="success"
                            onClick={() => openWhatsAppInbox(atendimento.conversation_id)}
                            size="small"
                          >
                            <WhatsAppIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {emAtendimento.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" color="text.secondary">
                      Nenhum atendimento em andamento
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog do Chat */}
      <Dialog
        open={chatDialog.open}
        onClose={() => setChatDialog({ open: false, messages: [], title: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{chatDialog.title}</DialogTitle>
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {chatDialog.messages.map((message, index) => (
                <React.Fragment key={message.id}>
                  <ListItem alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Avatar
                            sx={{
                              width: 24,
                              height: 24,
                              mr: 1,
                              bgcolor: message.is_from_user ? 'primary.main' : 'secondary.main',
                            }}
                          >
                            {message.is_from_user ? <PersonIcon fontSize="small" /> : <ChatIcon fontSize="small" />}
                          </Avatar>
                          <Typography variant="subtitle2">
                            {message.is_from_user ? 'Cliente' : 'Chatbot'}
                          </Typography>
                          <Typography variant="caption" sx={{ ml: 'auto' }}>
                            {formatDate(message.created_at)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.primary"
                          sx={{ mt: 1, display: 'block' }}
                        >
                          {message.content}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < chatDialog.messages.length - 1 && <Divider />}
                </React.Fragment>
              ))}
              {chatDialog.messages.length === 0 && (
                <Box textAlign="center" p={3}>
                  <Typography variant="body1" color="text.secondary">
                    Nenhuma mensagem encontrada
                  </Typography>
                </Box>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChatDialog({ open: false, messages: [], title: '' })}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FilaAtendimentos; 
